import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
    Box, Typography, Paper, TextField, Button, Grid2 as Grid,
    Alert, Chip, CircularProgress
} from "@mui/material";
import {
    PercentOutlined, SaveOutlined, OpenInNewOutlined,
    CheckCircleOutline, ErrorOutline
} from "@mui/icons-material";
import { getStripeSettings, updateStripeSettings } from "../../services/stripeSettingsApi";
import { useUI } from "../Context/UIContext";

// Sezione branding gestita solo dalla Dashboard Stripe (non modificabile via API per l'account proprio)
const STRIPE_BRANDING_URL = "https://dashboard.stripe.com/settings/branding";

// Stile comune delle card di sezione (coerente con il resto dell'admin)
const sectionSx = {
    p: 3,
    borderRadius: "20px",
    border: "1px solid #e2e8f0",
    mb: 3,
};

const SectionTitle = ({ icon, title, description }) => (
    <Box sx={{ mb: { xs: 3, md: 4 }, textAlign: "center" }}>
        <Typography
            variant="h5"
            sx={{ fontWeight: 800, letterSpacing: "-0.02em", color: "#0b3d87", lineHeight: 1.15, display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}
        >
            {icon} {title}
        </Typography>
        {description && (
            <Typography variant="body2" sx={{ mt: 0.75, color: "#64748b", fontWeight: 500 }}>
                {description}
            </Typography>
        )}
        <Box
            sx={{
                mt: 1.5,
                mx: "auto",
                width: 56,
                height: 4,
                borderRadius: 999,
                background: "linear-gradient(90deg, #0b3d87 0%, #345ea3 100%)",
            }}
        />
    </Box>
);

SectionTitle.propTypes = {
    icon: PropTypes.node.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
};

const emptyAddress = { street: "", city: "", state: "", postalCode: "", country: "" };

const StripeSettings = () => {
    const { showSuccess, showError } = useUI();
    const [enableAutomaticTax, setEnableAutomaticTax] = useState(false);
    const [headOfficeAddress, setHeadOfficeAddress] = useState(emptyAddress);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const data = await getStripeSettings();
                if (data) {
                    setEnableAutomaticTax(Boolean(data.enableAutomaticTax));
                    setHeadOfficeAddress(data.headOfficeAddress || emptyAddress);
                }
            } catch (err) {
                console.error(err);
                showError(err.message || "Errore nel recupero delle impostazioni Stripe");
            }
        };
        fetchSettings();
    }, [showError]);

    const handleAddressChange = (e) => {
        const { name, value } = e.target;
        setHeadOfficeAddress({ ...headOfficeAddress, [name]: value });
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            await updateStripeSettings({ enableAutomaticTax, headOfficeAddress });
            showSuccess("Impostazioni aggiornate con successo");
            const data = await getStripeSettings();
            if (data) {
                setEnableAutomaticTax(Boolean(data.enableAutomaticTax));
                setHeadOfficeAddress(data.headOfficeAddress || emptyAddress);
            }
        } catch (err) {
            console.error(err);
            showError(err.message || "Errore durante l'aggiornamento");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Alert
                severity="info"
                sx={{ borderRadius: "14px", mb: 3, alignItems: "center" }}
                action={
                    <Button
                        href={STRIPE_BRANDING_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        startIcon={<OpenInNewOutlined />}
                        sx={{ textTransform: "none", fontWeight: 700 }}
                    >
                        Dashboard Stripe
                    </Button>
                }
            >
                Colori, logo e dati aziendali pubblici della fattura si configurano dalla Dashboard Stripe.
            </Alert>

            <Paper elevation={3} sx={sectionSx}>
                <SectionTitle 
                   icon={<PercentOutlined />} 
                   title="Calcolo IVA e Sede Legale" 
                   description="Configura la sede legale aziendale utilizzata da Stripe per calcolare automaticamente le tasse sui checkout."
                />

                <Chip
                    icon={enableAutomaticTax ? <CheckCircleOutline /> : <ErrorOutline />}
                    label={enableAutomaticTax ? "Calcolo automatico tasse attivo" : "Calcolo automatico non ancora attivo"}
                    color={enableAutomaticTax ? "success" : "default"}
                    variant="outlined"
                    sx={{ mb: 2.5, fontWeight: 600 }}
                />

                <TextField label="Indirizzo (Via e civico)" name="street" value={headOfficeAddress.street || ""} onChange={handleAddressChange} fullWidth margin="dense" />
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField label="Città" name="city" value={headOfficeAddress.city || ""} onChange={handleAddressChange} fullWidth margin="dense" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField label="Provincia" name="state" value={headOfficeAddress.state || ""} onChange={handleAddressChange} fullWidth margin="dense" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField label="CAP" name="postalCode" value={headOfficeAddress.postalCode || ""} onChange={handleAddressChange} fullWidth margin="dense" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField label="Nazione (es. IT)" name="country" value={headOfficeAddress.country || ""} onChange={handleAddressChange} fullWidth margin="dense" />
                    </Grid>
                </Grid>
            </Paper>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                <Button
                    variant="contained"
                    size="large"
                    onClick={handleSave}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SaveOutlined />}
                    sx={{ backgroundColor: "#0b3d87", textTransform: "none", borderRadius: "12px", px: 4, fontWeight: 700, "&:hover": { backgroundColor: "#092f6b" } }}
                >
                    {loading ? "Salvataggio in corso..." : "Salva Impostazioni"}
                </Button>
            </Box>
        </Box>
    );
};

export default StripeSettings;
 